import { AddCategoryModal } from '@/components/admin/AddCategoryModal';
import { CategoriesView } from '@/components/admin/CategoriesView';
import { ItemEditorModal } from '@/components/admin/ItemEditorModal';
import { ItemsView } from '@/components/admin/ItemsView';
import { NewItemModal } from '@/components/admin/NewItemModal';
import { SystemPromptView } from '@/components/admin/SystemPromptView';
import { segClasses } from '@/components/admin/ui';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { PlusIcon } from '@/components/common/icons';
import { SCREEN } from '@/components/common/surfaces';
import { useAdminData } from '@/hooks/useAdminData';
import { useRoles } from '@/hooks/useRoles';
import { usernameOfUser } from '@/utils/auth';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Link, Navigate, useNavigate } from 'react-router-dom';

export type AdminView = 'categories' | 'items' | 'system';

const VIEW_PATH: Record<AdminView, string> = {
  categories: '/admin',
  items: '/admin/items',
  system: '/admin/system-prompt',
};

const ADD_BUTTON =
  'flex cursor-pointer items-center gap-[7px] rounded border border-primary bg-primary px-3 py-[7px] text-xs font-semibold text-white hover:bg-primary-deep';

/**
 * `/admin`, `/admin/items`, `/admin/system-prompt` — the prototype's administration: the back
 * link, the three views ("Systémový prompt" only for superadmins) and the add button of the view.
 */
export const Admin: FC<{ view: AdminView }> = ({ view }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { isSuperadmin } = useRoles();
  const me = usernameOfUser(auth.user);
  const data = useAdminData();
  const [editing, setEditing] = useState<string | null>(null);
  const [adding, setAdding] = useState<'category' | 'item' | null>(null);

  if (view === 'system' && !isSuperadmin) return <Navigate to="/admin" replace={true} />;

  const views: AdminView[] = isSuperadmin ? ['categories', 'items', 'system'] : ['categories', 'items'];
  const editedItem = editing ? data.items.find((item) => item.id === editing) : undefined;

  return (
    <div className={`${SCREEN} justify-center px-7 pb-14 pt-7`}>
      <div className="w-full max-w-[1080px]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="rounded border border-control bg-white px-3 py-[7px] text-xs font-semibold text-muted hover:border-primary hover:text-primary"
            >
              {t('admin.back')}
            </Link>
            <nav className="flex overflow-hidden rounded border border-control bg-white">
              {views.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  aria-current={entry === view ? 'page' : undefined}
                  onClick={() => navigate(VIEW_PATH[entry])}
                  className={segClasses(entry === view)}
                >
                  {t(`admin.views.${entry}`)}
                </button>
              ))}
            </nav>
          </div>
          {view === 'categories' ? (
            <button type="button" className={ADD_BUTTON} onClick={() => setAdding('category')}>
              <PlusIcon size={13} />
              {t('admin.addCategory')}
            </button>
          ) : null}
          {view === 'items' ? (
            <button type="button" className={ADD_BUTTON} onClick={() => setAdding('item')}>
              <PlusIcon size={13} />
              {t('admin.addItem')}
            </button>
          ) : null}
        </div>

        {view !== 'system' && data.error ? <ErrorMessage message={data.error} /> : null}
        {view !== 'system' && data.isLoading ? (
          <p className="m-0 text-[12.5px] text-subtle" role="status">
            {t('common.loading')}
          </p>
        ) : null}

        {view === 'categories' && !data.isLoading ? (
          <CategoriesView data={data} onOpenEditor={(item) => setEditing(item.id)} />
        ) : null}
        {view === 'items' && !data.isLoading ? (
          <ItemsView data={data} me={me} onOpenEditor={(item) => setEditing(item.id)} />
        ) : null}
        {view === 'system' ? <SystemPromptView me={me} /> : null}

        {editedItem ? (
          <ItemEditorModal
            key={editedItem.id}
            item={editedItem}
            onSave={(body) => data.updateItem(editedItem.id, body)}
            onClose={() => setEditing(null)}
          />
        ) : null}
        {adding === 'category' ? (
          <AddCategoryModal
            categories={data.categories}
            onCreate={data.createCategory}
            onClose={() => setAdding(null)}
          />
        ) : null}
        {adding === 'item' ? (
          <NewItemModal
            categories={data.categories}
            existingIds={new Set(data.items.map((item) => item.id))}
            onCreate={data.createItem}
            onClose={() => setAdding(null)}
          />
        ) : null}
      </div>
    </div>
  );
};
